import { useEffect, useRef, useState, type KeyboardEvent, type SyntheticEvent } from 'react';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';

export interface EditableRateCellProps {
  /** Accessible name for both the edit button and the input, e.g. "Edit Regular rate for Hourly". */
  label: string;
  /** Raw value the input starts from (unformatted number or plain text). */
  value: string;
  /** Formatted text shown when not editing. */
  displayText: string;
  numeric: boolean;
  onCommit: (value: string) => void;
}

const stop = (event: SyntheticEvent) => event.stopPropagation();

export function isValidRateInput(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  return /^-?\d+(\.\d+)?$/.test(trimmed);
}

/**
 * An in-place editable cell. Idle: a named button with a persistent pencil. Editing: an input
 * (decimal keypad for money); Enter/blur commits a changed valid value, Escape cancels. Events
 * never bubble to the host canvas (no host double-click, no block deselect).
 */
export function EditableRateCell({ label, value, displayText, numeric, onCommit }: EditableRateCellProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = draft !== null;
  const invalid = editing && numeric && !isValidRateInput(draft);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    if (next === value.trim()) return;
    if (numeric && !isValidRateInput(next)) return;
    onCommit(next);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter') commit();
    if (event.key === 'Escape') setDraft(null);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        aria-label={label}
        aria-invalid={invalid}
        inputMode={numeric ? 'decimal' : 'text'}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onInputKeyDown}
        onBlur={commit}
        onClick={stop}
        onDoubleClick={stop}
        style={{ width: '100%', font: 'inherit', textAlign: numeric ? 'right' : 'left', boxSizing: 'border-box' }}
      />
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      data-price-book-editable-cell="true"
      onClick={(event) => {
        event.stopPropagation();
        setDraft(value);
      }}
      onDoubleClick={stop}
      onKeyDown={stop}
      style={{
        background: 'none',
        border: 0,
        padding: 0,
        font: 'inherit',
        color: 'inherit',
        cursor: 'text',
        display: 'inline-flex',
        gap: 4,
        alignItems: 'baseline',
        width: '100%',
        justifyContent: numeric ? 'flex-end' : 'flex-start'
      }}
    >
      <span>{displayText}</span>
      <span aria-hidden="true" data-canvas-affordance="pencil" style={{ color: TEXT_COLORS.MUTED, fontSize: 10 }}>
        ✎
      </span>
    </button>
  );
}
